import React, { useState } from 'react';
import {
    View,
    Text,
    TextInput,
    TouchableOpacity,
    StyleSheet,
    KeyboardAvoidingView,
    Platform,
    ScrollView,
    ActivityIndicator,
    StatusBar,
} from 'react-native';
import { COLORS, APP_INFO } from '../config';
import { useAuth } from '../context/AuthContext';
import { useCustomAlert } from '../components/CustomAlert';

export default function RegisterScreen({ navigation }) {
    const [nama, setNama] = useState('');
    const [telepon, setTelepon] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const { register } = useAuth();
    const { alert } = useCustomAlert();

    const handleRegister = async () => {
        if (!nama || !telepon || !password) {
            alert('Error', 'Nama, telepon, dan password wajib diisi!');
            return;
        }

        if (password.length < 6) {
            alert('Error', 'Password minimal 6 karakter!');
            return;
        }

        if (password !== confirmPassword) {
            alert('Error', 'Konfirmasi password tidak sama!');
            return;
        }

        setLoading(true);
        const result = await register(nama, telepon, email, password);
        setLoading(false);

        if (result.success) {
            // Navigate back to login first
            navigation.goBack();
            // Then show success alert (will appear on login screen)
            setTimeout(() => {
                alert('Sukses! 🎉', result.message || 'Pendaftaran berhasil! Silakan login dengan akun baru Anda.');
            }, 300);
        } else {
            alert('Pendaftaran Gagal', result.message || 'Terjadi kesalahan saat mendaftar.');
        }
    };

    return (
        <KeyboardAvoidingView
            behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
            style={styles.container}
        >
            <StatusBar barStyle="dark-content" backgroundColor={COLORS.bgDarker} />
            <ScrollView
                contentContainerStyle={styles.scrollContent}
                keyboardShouldPersistTaps="handled"
                showsVerticalScrollIndicator={false}
            >
                {/* Header */}
                <View style={styles.header}>
                    <Text style={styles.brandName}>{APP_INFO.fullName}</Text>
                </View>

                {/* Benefits */}
                <View style={styles.benefitsCard}>
                    <Text style={styles.benefitsTitle}>Keuntungan Member</Text>
                    <Text style={styles.benefitItem}>✓ Kumpulkan {APP_INFO.currency}</Text>
                    <Text style={styles.benefitItem}>✓ Bayar dengan Minguro topup</Text>
                    <Text style={styles.benefitItem}>✓ Tukar poin dengan hadiah</Text>
                    <Text style={styles.benefitItem}>✓ Klaim voucher eksklusif</Text>
                </View>

                {/* Register Form */}
                <View style={styles.formCard}>
                    <Text style={styles.formTitle}>Daftar Member</Text>

                    <View style={styles.inputGroup}>
                        <Text style={styles.label}>Nama Lengkap *</Text>
                        <TextInput
                            style={styles.input}
                            placeholder="Nama lengkap"
                            placeholderTextColor={COLORS.textMuted}
                            value={nama}
                            onChangeText={setNama}
                        />
                    </View>

                    <View style={styles.inputGroup}>
                        <Text style={styles.label}>Nomor Telepon *</Text>
                        <TextInput
                            style={styles.input}
                            placeholder="08xxxxxxxxxx"
                            placeholderTextColor={COLORS.textMuted}
                            keyboardType="phone-pad"
                            value={telepon}
                            onChangeText={setTelepon}
                        />
                    </View>

                    <View style={styles.inputGroup}>
                        <Text style={styles.label}>Email (Opsional)</Text>
                        <TextInput
                            style={styles.input}
                            placeholder="email@contoh.com"
                            placeholderTextColor={COLORS.textMuted}
                            keyboardType="email-address"
                            value={email}
                            onChangeText={setEmail}
                        />
                    </View>

                    <View style={styles.inputGroup}>
                        <Text style={styles.label}>Password *</Text>
                        <TextInput
                            style={styles.input}
                            placeholder="Minimal 6 karakter"
                            placeholderTextColor={COLORS.textMuted}
                            secureTextEntry
                            value={password}
                            onChangeText={setPassword}
                        />
                    </View>

                    <View style={styles.inputGroup}>
                        <Text style={styles.label}>Konfirmasi Password *</Text>
                        <TextInput
                            style={styles.input}
                            placeholder="Ulangi password"
                            placeholderTextColor={COLORS.textMuted}
                            secureTextEntry
                            value={confirmPassword}
                            onChangeText={setConfirmPassword}
                        />
                    </View>

                    <TouchableOpacity
                        style={[styles.button, loading && styles.buttonDisabled]}
                        onPress={handleRegister}
                        disabled={loading}
                        activeOpacity={0.8}
                    >
                        {loading ? (
                            <ActivityIndicator color="#fff" />
                        ) : (
                            <Text style={styles.buttonText}>Daftar Sekarang</Text>
                        )}
                    </TouchableOpacity>
                </View>

                {/* Login Link */}
                <TouchableOpacity
                    style={styles.loginLink}
                    onPress={() => navigation.goBack()}
                >
                    <Text style={styles.loginText}>
                        Sudah punya akun?{' '}
                        <Text style={styles.loginLinkText}>Masuk</Text>
                    </Text>
                </TouchableOpacity>
            </ScrollView>
        </KeyboardAvoidingView>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: COLORS.bgDark,
    },
    scrollContent: {
        flexGrow: 1,
        padding: 24,
        paddingTop: 50,
        paddingBottom: 40,
    },
    header: {
        alignItems: 'center',
        marginBottom: 20,
    },
    brandName: {
        fontSize: 24,
        fontWeight: '800',
        color: COLORS.primary,
    },
    benefitsCard: {
        backgroundColor: COLORS.primary + '10',
        borderRadius: 16,
        padding: 16,
        marginBottom: 20,
        borderWidth: 1,
        borderColor: COLORS.primary + '20',
    },
    benefitsTitle: {
        color: COLORS.primary,
        fontWeight: '700',
        marginBottom: 10,
        fontSize: 14,
    },
    benefitItem: {
        color: COLORS.textSecondary,
        marginBottom: 4,
        fontSize: 13,
    },
    formCard: {
        backgroundColor: COLORS.bgCard,
        borderRadius: 20,
        padding: 24,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.1,
        shadowRadius: 12,
        elevation: 5,
    },
    formTitle: {
        fontSize: 20,
        fontWeight: '700',
        color: COLORS.textPrimary,
        marginBottom: 20,
    },
    inputGroup: {
        marginBottom: 16,
    },
    label: {
        color: COLORS.textSecondary,
        marginBottom: 8,
        fontWeight: '500',
        fontSize: 13,
    },
    input: {
        backgroundColor: COLORS.bgDark,
        borderRadius: 12,
        padding: 14,
        color: COLORS.textPrimary,
        fontSize: 15,
        borderWidth: 1,
        borderColor: COLORS.border,
    },
    button: {
        backgroundColor: COLORS.primary,
        borderRadius: 12,
        padding: 16,
        alignItems: 'center',
        marginTop: 8,
    },
    buttonDisabled: {
        opacity: 0.7,
    },
    buttonText: {
        color: '#fff',
        fontSize: 16,
        fontWeight: '600',
    },
    loginLink: {
        marginTop: 20,
        alignItems: 'center',
    },
    loginText: {
        color: COLORS.textSecondary,
        fontSize: 14,
    },
    loginLinkText: {
        color: COLORS.primary,
        fontWeight: '600',
    },
});
